import type { PendingEvent } from '../game/events'
import { lt, normalizeLocalizedBio } from '../game/localized'
import { ensureSurvivorSkills } from '../game/skills'
import {
  legacyAllBuiltWorkstationStates,
  normalizeShelterHideout,
  serializeShelterHideout,
} from '../game/hideoutProgression'
import {
  DEFAULT_GAME_SETTINGS,
  type Expedition,
  type GameSettings,
  type HideoutTierId,
  type ItemStack,
  type LocationExplorationState,
  type LocationId,
  type PetInstance,
  type QuestRuntimeState,
  type Survivor,
  type SurvivorGender,
  type VehicleInstance,
  type WorkstationId,
  type WorkstationRuntimeState,
} from '../game/types'
import { DEFAULT_QUEST_STATE, mergeQuestRuntimeState } from '../game/quests'
import { supabase } from './supabase'

export type SyncActionType =
  | 'tickResolved'
  | 'startExpedition'
  | 'runWorkstation'
  | 'resolveEvent'
  | 'settingsUpdated'
  | 'inventoryUpdated'
  | 'craftRecipe'
  | 'onboardingUpdated'
  | 'questsUpdated'
  | 'hydrateFromCloud'
  | 'seedCloudState'

type SyncAction = {
  type: SyncActionType
  at: number
}

export type SyncedGameState = {
  survivors: Survivor[]
  inventory: ItemStack[]
  expeditions: Expedition[]
  pendingEvents: PendingEvent[]
  progress: number
  locationExploration: Partial<Record<LocationId, LocationExplorationState>>
  xp: number
  /** Stash € balance — mirrors `progress.currency` server-side */
  trinketPoints: number
  onboardingStep: string
  settings: GameSettings
  hideoutTierId: HideoutTierId
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  quests: QuestRuntimeState
  garageVehicles: VehicleInstance[]
  gems: number
  pets: PetInstance[]
}

export type PullStateResult =
  | { kind: 'no-profile' }
  | { kind: 'hydrated'; state: SyncedGameState }

export class EmptyPushAbortedError extends Error {
  constructor() {
    super('refusing to push empty save over non-empty cloud state')
    this.name = 'EmptyPushAbortedError'
  }
}

export function extractQuestsFromProfileSettings(
  raw: unknown,
): QuestRuntimeState | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const q = o.questsState
  if (!q || typeof q !== 'object') return null
  const blob = q as Record<string, unknown>
  const starter = blob.starterSurvival
  if (!starter || typeof starter !== 'object') return null
  return mergeQuestRuntimeState(q as Partial<QuestRuntimeState>)
}

export function extractShelterHideoutFromProfileSettings(
  raw: unknown,
): {
  tierId: HideoutTierId
  workstations: Record<WorkstationId, WorkstationRuntimeState>
} | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const blob = o.shelterHideout
  if (!blob || typeof blob !== 'object') return null
  return normalizeShelterHideout(blob as { tierId?: HideoutTierId; workstations?: Partial<Record<WorkstationId, WorkstationRuntimeState>> })
}

export function extractLocationExplorationFromProfileSettings(
  raw: unknown,
): Partial<Record<LocationId, LocationExplorationState>> | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const blob = o.locationExplorationState
  if (!blob || typeof blob !== 'object') return null
  return blob as Partial<Record<LocationId, LocationExplorationState>>
}

export function extractGemsFromProfileSettings(raw: unknown): number | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  return typeof o.gems === 'number' && Number.isFinite(o.gems) ? o.gems : null
}

export function extractGarageVehiclesFromProfileSettings(
  raw: unknown,
): VehicleInstance[] | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const blob = o.garageVehicles
  if (!Array.isArray(blob)) return null
  return blob as VehicleInstance[]
}

export function extractPetsFromProfileSettings(raw: unknown): PetInstance[] | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const blob = o.petsState
  if (!Array.isArray(blob)) return null
  return blob as PetInstance[]
}

export function normalizeRemoteSettings(raw: unknown): GameSettings {
  const d = DEFAULT_GAME_SETTINGS
  if (!raw || typeof raw !== 'object') return { ...d }
  const o = raw as Record<string, unknown>
  const language =
    o.language === 'en' ? 'en' : o.language === 'nl' ? 'nl' : d.language
  const notificationsEnabled =
    typeof o.notificationsEnabled === 'boolean'
      ? o.notificationsEnabled
      : d.notificationsEnabled
  const soundEnabled =
    typeof o.soundEnabled === 'boolean' ? o.soundEnabled : d.soundEnabled
  const theme = o.theme === 'light' ? 'light' : o.theme === 'dark' ? 'dark' : d.theme
  return { language, notificationsEnabled, soundEnabled, theme }
}

const syncQueue: SyncAction[] = []

// Persisted "dirty" flag. The in-memory `syncQueue` is lost on page refresh,
// which means a player who made progress and refreshed before the 5s autosave
// (or whose beforeunload POST got cancelled by the browser) would silently
// lose those changes — the next cold start would have an empty queue, skip the
// pre-flush in syncFromCloud, and let the stale cloud overwrite the newer
// localStorage. This flag survives refreshes so syncFromCloud knows to push
// before pulling. It's stored outside Zustand's persist payload so we can read
// it cheaply during early startup.
const PENDING_SYNC_KEY = 'nederfall-shelter-pending-sync-v1'

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

export const markPendingSync = () => {
  const ls = safeLocalStorage()
  if (!ls) return
  try {
    ls.setItem(PENDING_SYNC_KEY, '1')
  } catch {
    // Quota / private-mode failures are non-fatal; the in-memory queue still
    // covers the same-session case.
  }
}

export const clearPendingSync = () => {
  const ls = safeLocalStorage()
  if (!ls) return
  try {
    ls.removeItem(PENDING_SYNC_KEY)
  } catch {
    // Ignore; a stale flag only causes an extra no-op push on next boot.
  }
}

export const hasPendingSync = (): boolean => {
  const ls = safeLocalStorage()
  if (!ls) return false
  try {
    return ls.getItem(PENDING_SYNC_KEY) === '1'
  } catch {
    return false
  }
}

export const queueSyncAction = (type: SyncActionType) => {
  syncQueue.push({ type, at: Date.now() })
  markPendingSync()
}

export const hasQueuedSyncActions = () => syncQueue.length > 0

// Only actions that explicitly mutate inventory contents are worth logging.
// High-frequency background actions (tickResolved, startExpedition) are excluded
// to avoid bloat — they don't change inventory themselves.
const INVENTORY_ACTION_TYPES = new Set<SyncActionType>([
  'inventoryUpdated',
  'runWorkstation',
  'craftRecipe',
  'resolveEvent',
])

let lastLoggedInventoryHash = ''

function inventoryHash(inventory: ItemStack[]): string {
  return inventory
    .map((s) => `${s.defId}:${s.quantity}`)
    .sort()
    .join(',')
}

async function writeInventoryLog(
  client: NonNullable<typeof supabase>,
  userId: string,
  actionTypes: SyncActionType[],
  inventory: ItemStack[],
): Promise<void> {
  const hash = inventoryHash(inventory)
  if (hash === lastLoggedInventoryHash) return
  lastLoggedInventoryHash = hash
  const relevantType = actionTypes.find((t) => INVENTORY_ACTION_TYPES.has(t)) ?? actionTypes[0]
  await client.from('inventory_logs').insert({
    profile_id: userId,
    action_type: relevantType,
    inventory_snapshot: inventory,
  })
}

const msToIso = (ms: number) => new Date(ms).toISOString()
const isoToMs = (value: string) => new Date(value).getTime()

export const pullState = async (
  userId: string,
): Promise<PullStateResult | null> => {
  if (!supabase) return null

  const [
    profileResult,
    survivorsResult,
    inventoryResult,
    expeditionsResult,
    pendingEventsResult,
    progressResult,
  ] = await Promise.all([
    supabase
      .from('profiles')
      .select('settings, onboarding_step')
      .eq('id', userId)
      .maybeSingle(),
    supabase.from('survivors').select('*').eq('profile_id', userId),
    supabase.from('inventory').select('*').eq('profile_id', userId),
    supabase.from('expeditions').select('*').eq('profile_id', userId),
    supabase.from('pending_events').select('*').eq('profile_id', userId),
    supabase
      .from('progress')
      .select('expeditions_completed, xp, currency')
      .eq('profile_id', userId)
      .maybeSingle(),
  ])

  if (
    profileResult.error ||
    survivorsResult.error ||
    inventoryResult.error ||
    expeditionsResult.error ||
    pendingEventsResult.error ||
    progressResult.error
  ) {
    throw new Error(
      profileResult.error?.message ??
        survivorsResult.error?.message ??
        inventoryResult.error?.message ??
        expeditionsResult.error?.message ??
        pendingEventsResult.error?.message ??
        progressResult.error?.message ??
        'Failed to pull synced state',
    )
  }

  // Profile row is the authoritative signal that this user has a save.
  // Empty child tables for an existing profile is a legitimate state
  // (e.g. a player who consumed their last item) and must NOT be treated
  // as "no save" — doing so previously caused a fresh-camp overwrite.
  if (profileResult.data == null) {
    return { kind: 'no-profile' }
  }

  const rawProfileSettings = profileResult.data.settings ?? null
  const shelter = extractShelterHideoutFromProfileSettings(rawProfileSettings)
  const profileSettings = normalizeRemoteSettings(rawProfileSettings)
  const questsSynced =
    extractQuestsFromProfileSettings(rawProfileSettings) ?? DEFAULT_QUEST_STATE
  const locationExplorationSynced =
    extractLocationExplorationFromProfileSettings(rawProfileSettings) ?? {}
  const garageVehiclesSynced =
    extractGarageVehiclesFromProfileSettings(rawProfileSettings) ?? []
  const gemsSynced = extractGemsFromProfileSettings(rawProfileSettings)
  const petsSynced = extractPetsFromProfileSettings(rawProfileSettings) ?? []

  return {
    kind: 'hydrated',
    state: {
      settings: profileSettings,
      quests: questsSynced,
      onboardingStep:
        (profileResult.data.onboarding_step as string) ?? 'not_started',
      survivors: survivorsResult.data.map((row) => ({
        id: row.id as string,
        name: row.name as string,
        role: row.role as Survivor['role'],
        status: row.status as Survivor['status'],
        needs: row.needs as Survivor['needs'],
        skills: ensureSurvivorSkills(row.skills as Survivor['skills']),
        loadout: row.loadout as Survivor['loadout'],
        bio: normalizeLocalizedBio(row.bio, lt('', '')),
        gender: (row as { gender?: string }).gender as SurvivorGender | undefined,
        portrait: (row as { portrait?: string }).portrait ?? undefined,
      })),
      inventory: inventoryResult.data.map((row) => ({
        defId: row.def_id as string,
        quantity: row.quantity as number,
        locked: (row.locked as boolean) ?? false,
      })),
      expeditions: expeditionsResult.data.map((row) => ({
        id: row.id as string,
        locationId: row.location_id as Expedition['locationId'],
        survivorId: row.survivor_id as string,
        survivorIds: [row.survivor_id as string],
        startedAt: isoToMs(row.started_at as string),
        endsAt: isoToMs(row.ends_at as string),
        status: 'active' as const,
        diary: (row.diary as string[]) ?? [],
        notifiedAt:
          row.notified_at != null && typeof row.notified_at === 'string'
            ? isoToMs(row.notified_at)
            : undefined,
      })),
      pendingEvents: pendingEventsResult.data.map((row) => ({
        survivorId: row.survivor_id as string,
        event: row.event as PendingEvent['event'],
        baseLoot: (row.base_loot as ItemStack[]) ?? [],
      })),
      progress: (progressResult.data?.expeditions_completed as number) ?? 0,
      locationExploration: locationExplorationSynced,
      xp: (progressResult.data?.xp as number) ?? 0,
      trinketPoints: (progressResult.data?.currency as number) ?? 0,
      hideoutTierId: shelter?.tierId ?? 'forest_hideout',
      workstations:
        shelter?.workstations ?? legacyAllBuiltWorkstationStates(),
      garageVehicles: garageVehiclesSynced,
      gems: gemsSynced ?? 100,
      pets: petsSynced,
    },
  }
}

export const pushChanges = async (
  userId: string,
  state: SyncedGameState,
  userEmail?: string,
) => {
  if (!supabase) return
  const client = supabase
  // Allow pushing when the in-memory queue is empty but a previous session
  // marked unflushed work in localStorage. Without this, a cold-start pre-flush
  // could not recover the unpushed state and the subsequent pull would
  // overwrite newer localStorage with stale cloud data.
  if (syncQueue.length === 0 && !hasPendingSync()) return

  // Snapshot action types before the queue is cleared so we can log them.
  // On a cold-start recovery push the queue may be empty; fall back to a
  // synthetic 'hydrateFromCloud' marker so logging code has something to read.
  const pendingActionTypes: SyncActionType[] =
    syncQueue.length > 0 ? syncQueue.map((a) => a.type) : ['hydrateFromCloud']

  // Sanity guard: never wipe a non-empty cloud save with an empty local one.
  // Pushing zero survivors AND zero inventory items is only valid on a brand
  // new profile; in any other scenario it indicates a corrupted/unhydrated
  // local state and we must abort rather than destroy data.
  if (state.survivors.length === 0 && state.inventory.length === 0) {
    const [survivorCount, inventoryCount] = await Promise.all([
      client
        .from('survivors')
        .select('id', { count: 'exact', head: true })
        .eq('profile_id', userId),
      client
        .from('inventory')
        .select('def_id', { count: 'exact', head: true })
        .eq('profile_id', userId),
    ])
    if ((survivorCount.count ?? 0) > 0 || (inventoryCount.count ?? 0) > 0) {
      throw new EmptyPushAbortedError()
    }
  }

  const displayName = userEmail?.split('@')[0] || 'Survivor'

  const settingsPayload = {
    ...state.settings,
    shelterHideout: serializeShelterHideout(
      state.hideoutTierId,
      state.workstations,
    ),
    questsState: state.quests,
    locationExplorationState: state.locationExploration,
    garageVehicles: state.garageVehicles,
    gems: state.gems,
    petsState: state.pets,
  }

  const profileUpsert = client.from('profiles').upsert(
    {
      id: userId,
      display_name: displayName,
      onboarding_step: state.onboardingStep,
      settings: settingsPayload,
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  )

  // Survivors: upsert by (profile_id, id), then delete only rows whose
  // ids no longer exist locally. This avoids the destructive
  // delete-then-insert pattern that previously could wipe a profile's
  // survivors if the insert was skipped or failed.
  const survivorIds = state.survivors.map((s) => s.id)
  const survivorsUpsert =
    state.survivors.length > 0
      ? client.from('survivors').upsert(
          state.survivors.map((survivor) => ({
            id: survivor.id,
            profile_id: userId,
            name: survivor.name,
            role: survivor.role,
            status: survivor.status,
            needs: survivor.needs,
            skills: survivor.skills,
            loadout: survivor.loadout,
            bio: survivor.bio,
            gender: survivor.gender ?? null,
            portrait: survivor.portrait ?? null,
          })),
          { onConflict: 'profile_id,id' },
        )
      : Promise.resolve({ error: null })
  const survivorsCleanup = (
    survivorIds.length > 0
      ? client
          .from('survivors')
          .delete()
          .eq('profile_id', userId)
          .not('id', 'in', `(${survivorIds.map((id) => `"${id}"`).join(',')})`)
      : client.from('survivors').delete().eq('profile_id', userId)
  )

  // Inventory: upsert by (profile_id, def_id), then delete only stale def_ids.
  const inventoryDefIds = state.inventory.map((s) => s.defId)
  const inventoryUpsert =
    state.inventory.length > 0
      ? client.from('inventory').upsert(
          state.inventory.map((stack) => ({
            profile_id: userId,
            def_id: stack.defId,
            quantity: stack.quantity,
            locked: stack.locked ?? false,
            updated_at: new Date().toISOString(),
          })),
          { onConflict: 'profile_id,def_id' },
        )
      : Promise.resolve({ error: null })
  const inventoryCleanup = (
    inventoryDefIds.length > 0
      ? client
          .from('inventory')
          .delete()
          .eq('profile_id', userId)
          .not(
            'def_id',
            'in',
            `(${inventoryDefIds.map((id) => `"${id}"`).join(',')})`,
          )
      : client.from('inventory').delete().eq('profile_id', userId)
  )

  // Expeditions: upsert by id, then delete only stale ids.
  const expeditionIds = state.expeditions.map((e) => e.id)
  const expeditionsUpsert =
    state.expeditions.length > 0
      ? client.from('expeditions').upsert(
          state.expeditions.map((expedition) => ({
            id: expedition.id,
            profile_id: userId,
            location_id: expedition.locationId,
            survivor_id: expedition.survivorId,
            started_at: msToIso(expedition.startedAt),
            ends_at: msToIso(expedition.endsAt),
            diary: expedition.diary,
            notified_at:
              expedition.notifiedAt != null
                ? msToIso(expedition.notifiedAt)
                : null,
          })),
          { onConflict: 'id' },
        )
      : Promise.resolve({ error: null })
  const expeditionsCleanup = (
    expeditionIds.length > 0
      ? client
          .from('expeditions')
          .delete()
          .eq('profile_id', userId)
          .not(
            'id',
            'in',
            `(${expeditionIds.map((id) => `"${id}"`).join(',')})`,
          )
      : client.from('expeditions').delete().eq('profile_id', userId)
  )

  // Pending events: upsert by (profile_id, survivor_id), then delete only
  // rows whose survivor_id is no longer pending locally.
  const pendingSurvivorIds = state.pendingEvents.map((p) => p.survivorId)
  const pendingEventsUpsert =
    state.pendingEvents.length > 0
      ? client.from('pending_events').upsert(
          state.pendingEvents.map((pending) => ({
            profile_id: userId,
            survivor_id: pending.survivorId,
            event_id: pending.event.id,
            event: pending.event,
            base_loot: pending.baseLoot,
          })),
          { onConflict: 'profile_id,survivor_id' },
        )
      : Promise.resolve({ error: null })
  const pendingEventsCleanup = (
    pendingSurvivorIds.length > 0
      ? client
          .from('pending_events')
          .delete()
          .eq('profile_id', userId)
          .not(
            'survivor_id',
            'in',
            `(${pendingSurvivorIds.map((id) => `"${id}"`).join(',')})`,
          )
      : client.from('pending_events').delete().eq('profile_id', userId)
  )

  const progressUpsert = client.from('progress').upsert(
    {
      profile_id: userId,
      expeditions_completed: state.progress,
      xp: state.xp,
      currency: state.trinketPoints,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'profile_id' },
  )

  const [
    profileResult,
    survivorsUpsertResult,
    survivorsCleanupResult,
    inventoryUpsertResult,
    inventoryCleanupResult,
    expeditionsUpsertResult,
    expeditionsCleanupResult,
    pendingEventsUpsertResult,
    pendingEventsCleanupResult,
    progressResult,
  ] = await Promise.all([
    profileUpsert,
    survivorsUpsert,
    survivorsCleanup,
    inventoryUpsert,
    inventoryCleanup,
    expeditionsUpsert,
    expeditionsCleanup,
    pendingEventsUpsert,
    pendingEventsCleanup,
    progressUpsert,
  ])

  const firstError =
    profileResult.error ??
    survivorsUpsertResult.error ??
    survivorsCleanupResult.error ??
    inventoryUpsertResult.error ??
    inventoryCleanupResult.error ??
    expeditionsUpsertResult.error ??
    expeditionsCleanupResult.error ??
    pendingEventsUpsertResult.error ??
    pendingEventsCleanupResult.error ??
    progressResult.error

  if (firstError) {
    throw new Error(firstError.message)
  }

  syncQueue.length = 0
  // Only clear the persisted dirty flag once the push fully succeeded — if any
  // table errored above we re-throw and leave the flag set so the next boot
  // retries.
  clearPendingSync()

  // Write an inventory audit log when this push touched inventory. Fire-and-
  // forget: a logging failure must never block the game or surface an error.
  const hasInventoryAction = pendingActionTypes.some((t) =>
    INVENTORY_ACTION_TYPES.has(t),
  )
  if (hasInventoryAction) {
    void writeInventoryLog(client, userId, pendingActionTypes, state.inventory)
  }
}
